import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchExpenses,
  upsertExpense as apiUpsertExpense,
  deleteExpense as apiDeleteExpense,
  fetchSummary,
  fetchCategories,
  EXPENSES_KEYS,
} from '../api/expensesApi';
import type { Expense, ExpenseInput, SummaryResponse, CategoryOption } from '../api/expensesApi';

export type { Expense, ExpenseInput, SummaryResponse, CategoryOption };

/* ── Hook ──────────────────────────────────────────────────────────── */

export function useExpenses(month?: string, category?: string) {
  const queryClient = useQueryClient();

  // ── Fetch expenses for the selected month ──────────────────────────
  const expensesQuery = useQuery({
    queryKey: EXPENSES_KEYS.listFiltered({ month, category }),
    queryFn: () => fetchExpenses({ month, category }),
    staleTime: 30_000,
  });

  const expenses = expensesQuery.data?.items ?? [];

  // ── Fetch summary for the selected month ──────────────────────────
  const summaryQuery = useQuery({
    queryKey: EXPENSES_KEYS.summary(month),
    queryFn: () => fetchSummary(month),
    staleTime: 30_000,
  });

  const summary = summaryQuery.data ?? null;

  // ── Fetch categories (static, long staleTime) ─────────────────────
  const categoriesQuery = useQuery({
    queryKey: EXPENSES_KEYS.categories(),
    queryFn: fetchCategories,
    staleTime: 300_000,
  });

  const categories = categoriesQuery.data?.categories ?? [];

  // ── Mutations ──────────────────────────────────────────────────────
  const upsertMutation = useMutation({
    mutationFn: (input: ExpenseInput) => apiUpsertExpense(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPENSES_KEYS.all });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDeleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPENSES_KEYS.all });
    },
  });

  // ── Callbacks ──────────────────────────────────────────────────────
  const saveExpense = useCallback(
    (input: ExpenseInput) => upsertMutation.mutate(input),
    [upsertMutation],
  );

  const removeExpense = useCallback(
    (id: string) => deleteMutation.mutate(id),
    [deleteMutation],
  );

  return {
    expenses,
    summary,
    categories,
    loading: expensesQuery.isLoading || summaryQuery.isLoading,
    saving: upsertMutation.isPending || deleteMutation.isPending,
    saveExpense,
    removeExpense,
  } as const;
}
